import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { TimeseriesPoint } from '../../lib/panelTypes';

interface Props {
  data: TimeseriesPoint[];
  color?: string;
  height?: number;
  /** Series is a running total — labels the tooltip accordingly. */
  cumulative?: boolean;
}

// '2026-07-01' -> '01/07'
const fmtDay = (d: string) => {
  const parts = d.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d;
};

// 12345 -> '12.3K' so running totals fit the narrow Y axis.
const fmtTick = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format;

const TimeSeriesChart: React.FC<Props> = ({ data, color = '#4f46e5', height = 300, cumulative = false }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-gray-400" style={{ height }}>
        No data in this range
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="tsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={fmtDay}
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          axisLine={false}
          tickLine={false}
          minTickGap={24}
        />
        <YAxis allowDecimals={false} tickFormatter={fmtTick} tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={36} />
        <Tooltip labelFormatter={(l) => `Day: ${l}`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
        <Area type="monotone" dataKey="value" name={cumulative ? 'total so far' : 'value'} stroke={color} strokeWidth={2} fill="url(#tsFill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default TimeSeriesChart;
